// @utils/useIoOptionState.ts
import { useState, useCallback } from 'react';
import * as Option from "@utils/Option";
import { ok } from "@ps-bridge/ok";
import type { Ok } from "@ps-bridge/ok";
import type { IOts, OptionTs } from "@ps-bridge/interop";
import type { OptionStateHandle, StateRead, StateUpdate } from "@ps-bridge/capabilitiesTypes";
import { ioEffect } from '@utils/effects/branded';

/**
 * A custom hook for managing state as an Option.
 *
 * @param initialState - The initial state wrapped as an Option.
 * @returns An object containing:
 *   - read: the current Option state handle,
 *   - setSome: a function that accepts a plain value of type A and sets the state to Some(value), returning IO<Ok>,
 *   - setMaybe: a function that accepts an Option<A> and sets the state accordingly, returning IO<Ok>.
 */
export function useIoOptionState<A>(initialState: OptionTs<A>): OptionStateHandle<A> {
  const [value, setValue] = useState<OptionTs<A>>(initialState);

  // setSome: updates the state to Some(value) for a given raw value.
  const setSome = useCallback(
    (a: A): IOts<Ok> =>
      ioEffect(() => {
        setValue(Option.some(a));
        return ok;
      }),
    [],
  );

  // setMaybe: updates the state with the given Option<A>.
  const setMaybe = useCallback(
    (option: OptionTs<A>): IOts<Ok> =>
      ioEffect(() => {
        setValue(option);
        return ok;
      }),
    [],
  );

  const read = value as StateRead<OptionTs<A>>;
  const setSomeBranded = setSome as StateUpdate<A>;
  const setMaybeBranded = setMaybe as StateUpdate<OptionTs<A>>;

  return { read, setSome: setSomeBranded, setMaybe: setMaybeBranded };
}
